import { useSpark } from "@/lib/store";
import { Link } from "react-router-dom";
import { SunArc } from "@/components/SparkSun";
import { PersonalSun } from "@/components/PersonalSun";
import { ShareWithCoach } from "@/components/ShareWithCoach";
import GoldThreadWeave from "@/components/GoldThreadWeave";

const SECTION_LABELS = {
  strengths: "My Strengths",
  values: "My Values",
  energy_sources: "What gives me energy",
  energy_drains: "What drains me",
  conditions_for_thriving: "My Conditions for Thriving",
  limiting_narratives: "What I'm working through",
  direction: "My Direction",
  starting_point: "Why I came to Spark",
};

const Panel = ({ eyebrow, title, items, emptyHint, linkedThemes, testid, tint = "canvas" }) => {
  const bg =
    tint === "cool" ? "hsl(var(--spark-cool))"
    : tint === "warmth" ? "hsl(var(--spark-warmth))"
    : "transparent";
  return (
    <div data-testid={testid} className="relative rounded-2xl p-7"
         style={{ background: bg, border: tint === "canvas" ? "1px solid rgba(0,0,0,0.06)" : "1px solid rgba(0,0,0,0.04)" }}>
      {linkedThemes && linkedThemes.length > 0 && (
        <div className="absolute -top-3 left-6 flex flex-wrap gap-1.5">
          {linkedThemes.map(t => (
            <span key={t} className="rounded-full bg-white border border-[rgba(44,80,160,0.4)] text-[#1F3D80] text-[10px] font-medium uppercase tracking-[0.15em] px-2.5 py-1">
              <span className="inline-block h-1.5 w-1.5 rounded-full bg-[#2C50A0] mr-1.5 align-middle" />
              {t}
            </span>
          ))}
        </div>
      )}
      <p className="spark-eyebrow mb-3">{eyebrow}</p>
      <h3 className="text-2xl leading-snug text-[#17171C] font-medium tracking-tight">{title}</h3>
      {items && items.length > 0 ? (
        <ul className="mt-4 space-y-2">
          {items.map((it, i) => (
            <li key={i} className="text-[#17171C]">
              <div className="flex items-start gap-3">
                <span className="mt-2 h-1 w-1 rounded-full bg-[#2C50A0] shrink-0" />
                <span className="leading-relaxed">{typeof it === "string" ? it : it.label || it.theme}</span>
              </div>
              {it?.evidence_items?.length > 0 && (
                <details className="ml-4 mt-1" data-testid="compass-strength-evidence">
                  <summary className="cursor-pointer text-xs text-[#8A8A91] hover:text-[#1F3D80]">Supporting examples · {it.source_activity?.replace(/-/g, " ")}</summary>
                  <ul className="mt-2 space-y-1.5">
                    {it.evidence_items.map((e, j) => (
                      <li key={j} className="text-sm text-[#56565D] flex items-start gap-2">
                        <span className="mt-2 h-px w-3 bg-[#2C50A0] shrink-0" />
                        <span>{e.quote} <span className="text-[#8A8A91]">· {e.source_label}</span></span>
                      </li>
                    ))}
                  </ul>
                </details>
              )}
            </li>
          ))}
        </ul>
      ) : (
        <p className="mt-4 spark-voice text-[#8A8A91]">{emptyHint}</p>
      )}
    </div>
  );
};

export default function CareerCompass() {
  const { profile } = useSpark();
  if (!profile) return <div className="text-[#8A8A91]">Loading…</div>;

  const strengthItems = profile.strengths || [];
  const confirmedThreads = profile.gold_threads_confirmed || [];
  const plainThreads = confirmedThreads.filter(t => t.origin !== "weave");

  const linksBySection = {};
  for (const t of confirmedThreads) {
    for (const link of t.linked_to || []) {
      if (!linksBySection[link]) linksBySection[link] = [];
      if (!linksBySection[link].includes(t.theme)) linksBySection[link].push(t.theme);
    }
  }

  const reasons = profile.starting_point_input?.reasons || [];

  return (
    <div className="space-y-14">
      <header className="relative">
        <div className="absolute -right-6 -top-16 opacity-[0.16] pointer-events-none hidden md:block">
          <SunArc size={220} color="#C3A56B" strokeWidth={1} />
        </div>
        <p className="spark-eyebrow-copper spark-eyebrow-with-dot">Your Career Compass</p>
        <h1 className="mt-3 text-4xl sm:text-5xl leading-tight text-[#17171C] font-medium tracking-tight">
          A living map of <span className="spark-voice text-[#1F3D80]">the person you're becoming.</span>
        </h1>
        <p className="mt-6 max-w-2xl text-lg text-[#56565D] leading-relaxed">
          Everything Spark surfaces that you confirm — or correct — is held here. Over time,
          Gold Threads connect these insights across strengths, energy, values and the
          conditions that let you thrive.
        </p>
        <div className="mt-5 flex items-center gap-4 text-sm">
          <span className="text-[#8A8A91]">Private to you.</span>
          <ShareWithCoach />
        </div>
      </header>

      <div className="spark-thread" />

      <PersonalSun compact />

      <div className="spark-thread" />

      {reasons.length > 0 && (
        <section>
          <p className="spark-eyebrow mb-4">Why I came to Spark</p>
          <div className="flex flex-wrap gap-2">
            {reasons.map(r => (
              <span key={r} data-testid="compass-original-reason" className="rounded-full border border-[rgba(0,0,0,0.12)] bg-white px-3 py-1.5 text-sm text-[#56565D]">{r}</span>
            ))}
          </div>
        </section>
      )}

      {profile.starting_point_summary && (
        <section className="spark-voice-block">
          <p className="spark-eyebrow mb-3">What I hoped Spark would help with</p>
          <p data-testid="compass-starting-point" className="spark-voice text-2xl leading-snug text-[#17171C] max-w-3xl">
            {profile.starting_point_summary}
          </p>
        </section>
      )}

      <div className="grid md:grid-cols-2 gap-6 pt-2">
        <Panel testid="compass-strengths" eyebrow="What I bring" title={SECTION_LABELS.strengths} items={strengthItems} emptyHint="Strengths will appear here as you work through your journey." linkedThemes={linksBySection.strengths} />
        <Panel testid="compass-values" eyebrow="What matters" title={SECTION_LABELS.values} items={profile.values} emptyHint="Values will emerge from your reflections on when work felt right." linkedThemes={linksBySection.values} tint="cool" />
        <Panel testid="compass-energy-sources" eyebrow="Energy" title={SECTION_LABELS.energy_sources} items={profile.energy_sources} emptyHint="Come back after Energy Map." linkedThemes={linksBySection.energy_sources} tint="warmth" />
        <Panel testid="compass-energy-drains" eyebrow="Energy" title={SECTION_LABELS.energy_drains} items={profile.energy_drains} emptyHint="Come back after Energy Map." linkedThemes={linksBySection.energy_drains} />
        <Panel testid="compass-tensions" eyebrow="Held, not resolved" title="Tensions I'm holding" items={profile.tensions} emptyHint="Where two true things pull against each other, Spark will hold them here — for you to make meaning from." tint="warmth" />
        <Panel testid="compass-conditions" eyebrow="Environment" title={SECTION_LABELS.conditions_for_thriving} items={profile.conditions_for_thriving} emptyHint="Come back after Conditions for Thriving." linkedThemes={linksBySection.conditions_for_thriving} tint="cool" />
        <Panel testid="compass-limiting" eyebrow="Inner narrative" title={SECTION_LABELS.limiting_narratives} items={profile.limiting_narratives} emptyHint="Come back after Confidence & Inner Narrative." linkedThemes={linksBySection.limiting_narratives} />
      </div>

      <GoldThreadWeave />

      {plainThreads.length > 0 && (
        <section>
          <div className="flex items-center gap-3 mb-4">
            <div className="h-px w-8" style={{ background: "linear-gradient(90deg, transparent, #C3A56B)" }} />
            <span className="spark-eyebrow-copper">Gold Threads</span>
          </div>
          <p className="spark-voice text-2xl text-[#17171C] mb-4">Threads that run through everything.</p>
          <ul className="grid sm:grid-cols-2 gap-3" data-testid="compass-gold-threads">
            {plainThreads.map(t => (
              <li key={t.id} className="flex items-start gap-3 rounded-xl border border-[rgba(195,165,107,0.45)] p-4"
                  style={{ background: "hsl(var(--spark-warmth))" }}>
                <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-[#C3A56B]" />
                <span className="text-[#17171C] spark-voice text-lg leading-snug">{t.theme}</span>
              </li>
            ))}
          </ul>
          <p className="mt-3 text-xs text-[#8A8A91]">
            Each confirmed thread is tagged onto the panels above where it shows up.
          </p>
        </section>
      )}

      {profile.direction && (
        <section className="spark-voice-block">
          <p className="spark-eyebrow-copper mb-3">My Direction</p>
          <p data-testid="compass-direction" className="spark-voice text-3xl leading-snug text-[#17171C] max-w-3xl">{profile.direction}</p>
        </section>
      )}

      <div className="flex flex-wrap gap-3">
        <Link to="/part-one" data-testid="compass-continue-btn" className="spark-btn-primary">Continue my journey</Link>
        <Link to="/part-two" data-testid="compass-part-two-btn" className="spark-btn-secondary">See what's next</Link>
      </div>
    </div>
  );
}
